import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { HttpClient } from '../http/HttpClient.ts';
import type { AuditPage } from './OrgsApi.ts';

export interface Passkey {
  id: string;
  name: string;
  /** Synced across the person's devices by their password manager. */
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

/** The password, plus a code when two-factor is on (adding or removing a passkey). */
export type IdentityProof = { password: string } & (
  { code: string } | { recoveryCode: string } | Record<never, never>
);

/** Your passkeys and security history (/me/...). */
export class SecurityApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  passkeys(): Promise<{ passkeys: Passkey[] }> {
    return this.#http.get('/me/passkeys');
  }

  /** What the browser needs to create a passkey, and the ticket to finish with. */
  passkeyOptions(): Promise<{ options: PublicKeyCredentialCreationOptionsJSON; ticket: string }> {
    return this.#http.post('/me/passkeys/options');
  }

  addPasskey(input: {
    ticket: string;
    response: RegistrationResponseJSON;
    name: string;
    proof: IdentityProof;
  }): Promise<{ passkey: Passkey }> {
    return this.#http.post('/me/passkeys', input);
  }

  renamePasskey(id: string, name: string): Promise<{ passkey: Passkey }> {
    return this.#http.patch(`/me/passkeys/${encodeURIComponent(id)}`, { name });
  }

  removePasskey(id: string, proof: IdentityProof): Promise<void> {
    return this.#http.post(`/me/passkeys/${encodeURIComponent(id)}/remove`, { proof });
  }

  /** Your sign-ins and security changes, newest first. */
  history(after: string | null): Promise<AuditPage> {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    return this.#http.get(`/me/security-history${query}`);
  }
}
